/**
 * Login Form Component
 * Created: Unknown
 * Updated: 2025-01-21 - FIXED: Conditional Google login rendering for trade-webapp
 * Updated: 2025-01-22 - FIXED: Conditional signup link rendering for console-webapp
 * Updated: 2025-01-27 - CHANGED: Default email for trade-webapp changed from trader@frnt.io to client@frnt.io
 *
 * Changes:
 * - Accepts showGoogleLogin prop to control Google OAuth button visibility
 * - When showGoogleLogin=false, completely hides Google login section (no skeleton loaders)
 * - Used by login page to hide Google login for trade-webapp access
 * - Accepts showSignup prop to control "Sign up now" link visibility
 * - When showSignup=false, completely hides signup link section
 * - Used by login page to hide signup link for console-webapp access
 * - Default email for localhost development: admin@frnt.io for console-webapp, client@frnt.io for trade-webapp
 */

'use client'

import React, {useCallback, useMemo, useState} from 'react'
import {useRouter, useSearchParams} from 'next/navigation'
import {useReCaptcha} from '../../contexts/recaptcha.context'
import {useFlashMessage} from '../../hooks/useFlashMessage'
import {Box, Button, Divider, HStack, IconButton, InputRightElement, Link, Text, VStack} from '@chakra-ui/react'
import {ViewIcon, ViewOffIcon} from '@chakra-ui/icons'
import {useFlags} from 'flagsmith/react'
import qs from 'qs'
import {GoogleLoginButton} from '../auth/GoogleLoginButton'
import {Formik, Form} from 'formik'
import {FieldInput} from '@frnt/base/components'
import * as Yup from 'yup'

// Custom error class for API errors
class APIError extends Error {
  statusCode: number
  error: {message?: string}

  constructor(message: string, details: {statusCode: number; error: {message?: string}}) {
    super(message)
    this.name = 'APIError'
    this.statusCode = details.statusCode
    this.error = details.error
  }
}

interface LoginFormData {
  email: string
  password: string
}

interface LoginFormProps {
  showGoogleLogin?: boolean
  showSignup?: boolean
}

// Validation schema for the login form
const loginValidationSchema = Yup.object({
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string().required('Password is required').min(6, 'Password must be at least 6 characters')
})

export const LoginForm = ({showGoogleLogin = true, showSignup = true}: LoginFormProps) => {
  const router = useRouter()
  const searchParams = useSearchParams()
  const {showError} = useFlashMessage()
  const recaptchaContext = useReCaptcha()
  const [showPassword, setShowPassword] = useState(false)

  // Check Flagsmith feature flag for Google authentication
  const flags = useFlags(['google_auth'])
  const isGoogleAuthEnabled = flags?.google_auth?.enabled ?? true
  const shouldShowGoogleLogin = showGoogleLogin && isGoogleAuthEnabled

  // Get OAuth parameters
  const clientId = searchParams.get('client_id')
  const redirectUri = searchParams.get('redirect_uri')
  const responseType = searchParams.get('response_type')
  const scope = searchParams.get('scope')

  // Construct the password recover link with query parameters
  const passwordRecoverHref = () => {
    const params = new URLSearchParams()
    if (redirectUri) params.set('redirect_uri', redirectUri)
    if (clientId) params.set('client_id', clientId)
    return `/password/?${params.toString()}`
  }

  // Construct the signup link with all existing query parameters
  const signupHref = () => {
    const params = new URLSearchParams(searchParams.toString())
    return `/signup?${params.toString()}`
  }

  const handleSubmit = useCallback(
    async (values: LoginFormData, {setSubmitting}: {setSubmitting: (isSubmitting: boolean) => void}) => {
      try {
        // Get reCAPTCHA token if available
        let recaptchaToken = ''
        try {
          recaptchaToken = (await recaptchaContext.executeRecaptcha('login')) || ''
        } catch (recaptchaError) {
          console.warn('ReCAPTCHA execution failed:', recaptchaError)
        }

        // Proceed with login including reCAPTCHA token if available
        const response = await fetch('/api/auth/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...values,
            recaptcha_token: recaptchaToken,
            client_id: clientId,
            redirect_uri: redirectUri,
            response_type: responseType,
            scope
          })
        })

        if (!response.ok) {
          const error = await response.json()
          throw new APIError('Login failed', {
            statusCode: response.status,
            error
          })
        }

        const result = await response.json()

        // Check if 2FA is globally disabled for local development
        const is2faDisabled = process.env.NEXT_PUBLIC_IS_2FA_DISABLED === 'true'

        // Check if 2FA is required
        if (result.requires2FA && !is2faDisabled) {
          const twoFactorParams = {
            client_id: clientId,
            redirect_uri: redirectUri,
            response_type: responseType,
            scope,
            transaction_id: result.transactionId
          }

          if (result.needsSetup) {
            router.push(`/auth/2fa/setup?${qs.stringify(twoFactorParams)}`)
          } else {
            router.push(`/auth/2fa?${qs.stringify(twoFactorParams)}`)
          }
          return
        }

        // Check if we should proceed directly to OAuth authorization
        if (result.proceedToOAuth && result.redirect_url) {
          console.log('Proceeding to OAuth authorization:', result.redirect_url)
          const redirectUrl = result.redirect_url.startsWith('/') ? `${window.location.origin}${result.redirect_url}` : result.redirect_url
          window.location.href = redirectUrl
          return
        }

        // If login successful, redirect to authorization endpoint
        if (clientId && redirectUri && responseType && scope) {
          const accessToken = result.access_token
          localStorage.setItem('accessToken', accessToken)

          console.log('Login successful, redirecting to authorization endpoint with token:', accessToken)

          const authorizeUrl = `/oauth/authorize?${qs.stringify({
            client_id: clientId,
            redirect_uri: redirectUri,
            response_type: responseType,
            scope
          })}`

          const url = new URL(authorizeUrl, window.location.origin)
          window.location.href = url.toString()
        } else {
          router.push('/dashboard')
        }
      } catch (error) {
        console.error('Login failed:', error)
        showError(error instanceof APIError ? error.error?.message || error.message : 'An unexpected error occurred during login.')
      } finally {
        setSubmitting(false)
      }
    },
    [clientId, redirectUri, responseType, scope, router, showError, recaptchaContext]
  )

  const isLocalhost = useMemo(() => typeof window !== 'undefined' && window.location.hostname === 'localhost', [])

  const initialValues: LoginFormData = useMemo(() => {
    if (!isLocalhost) {
      return {email: '', password: ''}
    }
    const password = 'Test1234!'
    // showGoogleLogin is true for console, false for trade
    const email = showGoogleLogin ? 'admin@frnt.io' : 'client@frnt.io'
    return {email, password}
  }, [isLocalhost, showGoogleLogin])

  return (
    <Formik initialValues={initialValues} validationSchema={loginValidationSchema} onSubmit={handleSubmit}>
      {({isSubmitting}) => (
        <Form>
          <VStack spacing={5} align="stretch">
            <FieldInput name="email" label="Email" componentType="input" type="email" placeholder="Enter your email" size="md" />
            <FieldInput
              name="password"
              label="Password"
              componentType="input"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              size="md"
              inputRightElement={
                <InputRightElement>
                  <IconButton
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                    icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    variant="ghost"
                  />
                </InputRightElement>
              }
            />

            <HStack justify="flex-end">
              <Link href={passwordRecoverHref()} fontSize="sm" color="primary">
                Forgot password?
              </Link>
            </HStack>

            <Button type="submit" colorScheme="primary" size="md" width="full" isLoading={isSubmitting} loadingText="Signing in" height="42px">
              Sign in
            </Button>

            {shouldShowGoogleLogin && (
              <>
                <Box position="relative" py={3}>
                  <Divider />
                  <Text position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" bg="white" px={3} color="gray.500" fontSize="sm">
                    or
                  </Text>
                </Box>

                <GoogleLoginButton isDisabled={isSubmitting} />
              </>
            )}

            {showSignup && (
              <Text textAlign="center" fontSize="sm" color="gray.600">
                Don't have an account?{' '}
                <Link href={signupHref()} color="primary" fontWeight="bold">
                  Sign up now
                </Link>
              </Text>
            )}
          </VStack>
        </Form>
      )}
    </Formik>
  )
}
